import React from 'react';
import { AlertTriangle, History } from 'lucide-react';
import type {
  EligibilityCheckResponse,
  OtherAgencyPeriodOfCare,
} from '../../services/api';
import {
  formatDateForDisplay,
  formatDisplayDateInAgencyZone,
} from '../../utils/dateUtils';
import {
  checkAgeDays,
  ELIGIBILITY_STALE_AFTER_DAYS,
} from './eligibilityStatus';

interface OtherAgencyPeriodsOfCareProps {
  /** The check whose CMS answer to show, or null when none answered. */
  check: EligibilityCheckResponse | null;
}

function periodKey(period: OtherAgencyPeriodOfCare): string {
  return `${period.npi ?? ''}|${period.agency_name ?? ''}|${period.start_date ?? ''}|${period.end_date ?? ''}`;
}

function periodDates(period: OtherAgencyPeriodOfCare): string {
  const end = period.end_date ? formatDateForDisplay(period.end_date) : null;
  if (!period.start_date) return end ? `Until ${end}` : 'Dates unknown';
  return `${formatDateForDisplay(period.start_date)} – ${end ?? 'ongoing'}`;
}

function PeriodList({
  periods,
  earlier = false,
}: {
  periods: OtherAgencyPeriodOfCare[];
  earlier?: boolean;
}) {
  return (
    <ul className='space-y-0.5'>
      {periods.map(period => (
        <li
          key={periodKey(period)}
          className='flex flex-wrap items-baseline gap-x-3 gap-y-0.5 break-words'
        >
          <span className={earlier ? undefined : 'font-semibold'}>
            {earlier && 'Earlier: '}
            {period.agency_name ?? 'Unknown agency'}
          </span>
          {period.npi && (
            <span className='font-mono text-xs'>NPI {period.npi}</span>
          )}
          <span>{periodDates(period)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Home health periods of care CMS reported at another agency, as of the check.
 * Display only: it never blocks intake or decides coverage.
 */
export const OtherAgencyPeriodsOfCare: React.FC<
  OtherAgencyPeriodsOfCareProps
> = ({ check }) => {
  const periods = check?.other_agency_periods_of_care;
  if (!check || !periods || periods.length === 0) return null;

  const asOf = check.response_date ?? check.request_date;
  const stale = checkAgeDays(asOf) > ELIGIBILITY_STALE_AFTER_DAYS;
  const reported = `Reported by CMS on ${formatDisplayDateInAgencyZone(asOf)}`;
  const active = periods.filter(p => p.status === 'active');
  const ended = periods.filter(p => p.status === 'ended');

  // Amber, not red: an overlap needs attention but blocks nothing.
  if (active.length > 0 && !stale) {
    return (
      <div
        role='alert'
        className='relative rounded-lg border border-amber-300 bg-amber-50 p-4 pl-11 text-left text-amber-800'
      >
        <AlertTriangle
          aria-hidden='true'
          className='absolute left-4 top-[18px] h-4 w-4 text-amber-600'
        />
        <h5 className='mb-1.5 text-sm font-semibold text-amber-900'>
          On service with another agency
        </h5>
        <div className='text-sm text-amber-900'>
          <PeriodList periods={active} />
        </div>
        {ended.length > 0 && (
          <div className='mt-3 border-t border-amber-200 pt-2.5 text-xs text-amber-700'>
            <PeriodList periods={ended} earlier />
          </div>
        )}
        <p className='mt-2 text-xs text-amber-700'>{reported}</p>
      </div>
    );
  }

  return (
    <div
      role='status'
      className='relative rounded-lg border bg-muted p-4 pl-11 text-left text-muted-foreground'
    >
      <History
        aria-hidden='true'
        className='absolute left-4 top-[18px] h-4 w-4'
      />
      <h5 className='mb-1.5 text-sm font-semibold text-foreground'>
        {active.length > 0
          ? 'Was on service with another agency'
          : 'Earlier periods of care at other agencies'}
      </h5>
      <div className='text-sm'>
        <PeriodList periods={active.length > 0 ? active : ended} />
      </div>
      {active.length > 0 && ended.length > 0 && (
        <div className='mt-3 border-t pt-2.5 text-xs'>
          <PeriodList periods={ended} earlier />
        </div>
      )}
      <p className='mt-2 text-xs'>
        {stale ? `${reported}. Check eligibility again to confirm.` : reported}
      </p>
    </div>
  );
};
